import React from "react";
import PropTypes from "prop-types";
import { Modal } from "antd";
import { Icon, ICONS, EmptyState } from "./ui";
import {
    buildRequestGroups,
    buildDocRequest,
    docRequestError,
    requestNeedsApplicant,
    requestNeedsName
} from "./data-utils";

/**
 * Ask the customer for any document in the master — Document Review's request
 * flow in workspace style. KYC documents take the applicant and "Others" takes
 * a document name inline, instead of Document Review's second pop-up; the body
 * sent is the same (see buildDocRequest).
 */
class RequestDocsModal extends React.Component {
    state = { query: "", selected: [], details: {}, error: null };

    componentDidMount() {
        if (this.props.open) this.reset();
    }

    componentDidUpdate(prevProps) {
        if (this.props.open && !prevProps.open) this.reset();
    }

    /** Fresh state, with any preset (e.g. from a checklist row) ticked. */
    reset() {
        const known = new Set();
        buildRequestGroups(this.props.docMasterList).forEach(g => g.items.forEach(i => known.add(i.value)));
        const selected = (this.props.preset || []).map(String).filter(v => known.has(v));
        this.setState({ query: "", selected, details: {}, error: null });
    }

    toggle = value =>
        this.setState(({ selected }) => ({
            selected: selected.indexOf(value) >= 0 ? selected.filter(v => v !== value) : selected.concat(value),
            error: null
        }));

    setDetail = (value, patch) =>
        this.setState(({ details }) => ({
            details: { ...details, [value]: { ...(details[value] || {}), ...patch } },
            error: null
        }));

    submit = () => {
        const { selected, details } = this.state;
        const error = docRequestError(selected, details);
        if (error) {
            this.setState({ error });
            return;
        }
        this.props.onSubmit(buildDocRequest(selected, details));
    };

    render() {
        const { open, onClose, docMasterList, applicants, busy } = this.props;
        const { query, selected, details, error } = this.state;
        const groups = buildRequestGroups(docMasterList);
        const labels = {};
        groups.forEach(g => g.items.forEach(item => (labels[item.value] = item.label)));

        const q = query.trim().toLowerCase();
        const visible = groups
            .map(g => ({
                ...g,
                items: q
                    ? g.items.filter(
                        item => item.label.toLowerCase().indexOf(q) >= 0 || g.category.toLowerCase().indexOf(q) >= 0
                    )
                    : g.items
            }))
            .filter(g => g.items.length);
        const needsDetail = selected.filter(v => requestNeedsApplicant(v) || requestNeedsName(v));

        return (
            <Modal
                visible={open}
                destroyOnClose
                className={"ops-ws-modal"}
                wrapClassName={"ops-ws-modal-wrap"}
                width={680}
                // Above the checklist drawer (z-index 1011), which can open it.
                zIndex={1020}
                title={"Request documents"}
                okText={busy ? "Requesting…" : selected.length ? `Request ${selected.length}` : "Request"}
                okButtonProps={{ disabled: busy || !selected.length }}
                confirmLoading={busy}
                onOk={this.submit}
                onCancel={() => !busy && onClose()}
            >
                <input
                    className={"ops-ws__input"}
                    placeholder={"Search documents"}
                    value={query}
                    onChange={e => this.setState({ query: e.target.value })}
                />

                <div className={"ops-ws__req-body"}>
                    {!groups.length ? (
                        <EmptyState>{"Loading the document list…"}</EmptyState>
                    ) : !visible.length ? (
                        <EmptyState>{`Nothing matches "${query}".`}</EmptyState>
                    ) : (
                        visible.map(group => (
                            <div key={group.category} className={"ops-ws__req-group"}>
                                <div className={"ops-ws__eyebrow"}>{group.category}</div>
                                <div className={"ops-ws__chip-grid"}>
                                    {group.items.map(item => {
                                        const on = selected.indexOf(item.value) >= 0;
                                        return (
                                            <button
                                                key={item.value}
                                                type={"button"}
                                                className={"ops-ws__chip" + (on ? " is-active" : "")}
                                                disabled={busy}
                                                onClick={() => this.toggle(item.value)}
                                                title={item.label}
                                            >
                                                {on ? <Icon d={ICONS.check} size={12} width={2.6} /> : null}
                                                <span className={"ops-ws__chip-label"}>{item.label}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {needsDetail.length ? (
                    <div className={"ops-ws__req-details"}>
                        <div className={"ops-ws__eyebrow ops-ws__eyebrow--primary"}>{"Details needed"}</div>
                        {needsDetail.map(value => (
                            <div key={value} className={"ops-ws__req-detail"}>
                                <span className={"ops-ws__req-detail-name"}>{labels[value] || "Document"}</span>
                                {requestNeedsApplicant(value) ? (
                                    <select
                                        className={"ops-ws__select"}
                                        value={(details[value] && details[value].associate) || ""}
                                        disabled={busy}
                                        onChange={e =>
                                            this.setDetail(value, {
                                                associate: e.target.value ? Number(e.target.value) : null
                                            })
                                        }
                                    >
                                        <option value={""}>{"Select applicant"}</option>
                                        {applicants.map(a => (
                                            <option key={a.id} value={a.id}>
                                                {a.name}
                                            </option>
                                        ))}
                                    </select>
                                ) : (
                                    <input
                                        className={"ops-ws__input"}
                                        placeholder={"Document name (min 5 characters)"}
                                        value={(details[value] && details[value].remarks) || ""}
                                        disabled={busy}
                                        onChange={e => this.setDetail(value, { remarks: e.target.value })}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                ) : null}

                {error ? <div className={"ops-ws__field-error"} style={{ marginTop: 10 }}>{error}</div> : null}
            </Modal>
        );
    }
}

RequestDocsModal.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    docMasterList: PropTypes.any,
    preset: PropTypes.array,
    applicants: PropTypes.array.isRequired,
    busy: PropTypes.bool
};

export default RequestDocsModal;
